import { useEffect, useState } from "react";
import clsx from "clsx";
import type {
  SubmissionDetail,
  SubmissionItem,
  SubmissionStatus,
  SubmissionSummary,
  SubmissionTermsVersion,
  SubmissionTouch,
} from "@edge/shared";
import {
  fetchSubmissions,
  fetchSubmissionDetail,
  ApiError,
  patchItem,
  archiveTouch,
  archiveItem,
  archiveSubmission,
  retryItemZohoLink,
} from "../../api";
import { timeAgo } from "../../lib/relative-time";
import { STATUS_PILL_LABELS } from "../../lib/submission-status";
import { TabPanel } from "./TabPanel";
import { AddSubmissionDialog } from "./dialogs/AddSubmissionDialog";
import { ChangeStatusDialog } from "./dialogs/ChangeStatusDialog";
import { AddTouchDialog } from "./dialogs/AddTouchDialog";
import { AddItemDialog } from "./dialogs/AddItemDialog";
import { ConfirmDeleteDialog } from "../shared/ConfirmDeleteDialog";

/**
 * Pre-formats the right-side blurb for the compressed row. Status-driven.
 */
function summaryBlurb(s: SubmissionSummary): string | null {
  switch (s.status) {
    case "declined":
      return s.declineReason ? `"${s.declineReason}"` : null;
    case "backup":
      return s.backupReason ? `"${s.backupReason}"` : "parked";
    case "withdrawn":
      return "withdrawn";
    case "ghosted":
      return "no response";
    case "funded":
      return s.termsSummary ?? "funded";
    case "terms":
    case "negotiating":
      return s.termsSummary;
    case "need_info":
      return s.itemsProgress
        ? `${s.itemsProgress.sent}/${s.itemsProgress.total} items`
        : "items requested";
    case "pitched":
    case "reviewing":
      return null;
  }
}

/**
 * One of these slots is active at a time. The discriminator drives which
 * dialog renders. Carrying the SubmissionSummary directly lets dialogs
 * read the lender name + current status without an extra prop dance.
 */
type DialogState =
  | { kind: "add" }
  | { kind: "status"; sub: SubmissionSummary }
  | { kind: "touch"; sub: SubmissionSummary }
  | { kind: "item"; sub: SubmissionSummary }
  | { kind: "delete-touch"; sub: SubmissionSummary; touchId: string }
  | { kind: "delete-item"; sub: SubmissionSummary; itemId: string }
  | { kind: "archive-sub"; sub: SubmissionSummary };

interface SubmissionsTabProps {
  dealId: string;
  /** Deal name for the toast and the AddSubmissionDialog title. */
  dealName?: string;
}

export function SubmissionsTab({ dealId, dealName }: SubmissionsTabProps) {
  const [summaries, setSummaries] = useState<SubmissionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const bumpRefresh = () => setRefreshTick((t) => t + 1);

  useEffect(() => {
    let cancelled = false;
    if (refreshTick === 0) {
      setSummaries(null);
      setError(null);
    }
    fetchSubmissions(dealId)
      .then((r) => {
        if (!cancelled) setSummaries(r.submissions);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? `${err.code} (HTTP ${err.status})`
            : err instanceof Error
              ? err.message
              : "fetch_failed",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [dealId, refreshTick]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const dealLabel = dealName?.split(" - ")[0] ?? "this deal";

  // Loading first paint
  if (summaries === null && !error) {
    return (
      <TabPanel>
        <div className="submissions-loading">
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-label">LOADING SUBMISSIONS</span>
        </div>
      </TabPanel>
    );
  }

  if (error) {
    return (
      <TabPanel>
        <div className="write-confirm-error">
          ⚠ Failed to load submissions: {error}
        </div>
      </TabPanel>
    );
  }

  const rows = summaries ?? [];

  return (
    <TabPanel>
      <div className="submissions-tab">
        <div className="submissions-header">
          <div className="submissions-count">
            {rows.length === 0
              ? "NO ACTIVE SUBMISSIONS"
              : `${rows.length} ACTIVE SUBMISSION${rows.length === 1 ? "" : "S"}`}
          </div>
          <button
            className="submissions-add-btn"
            onClick={() => setDialog({ kind: "add" })}
          >
            + ADD SUBMISSION
          </button>
        </div>

        {rows.length === 0 ? (
          <EmptyState onAdd={() => setDialog({ kind: "add" })} />
        ) : (
          <div className="submissions-list">
            {rows.map((s) => (
              <SubmissionRow
                key={s.id}
                summary={s}
                expanded={expandedId === s.id}
                refreshTick={refreshTick}
                onToggle={() =>
                  setExpandedId((cur) => (cur === s.id ? null : s.id))
                }
                onChangeStatus={() => setDialog({ kind: "status", sub: s })}
                onAddTouch={() => setDialog({ kind: "touch", sub: s })}
                onAddItem={() => setDialog({ kind: "item", sub: s })}
                onArchiveSub={() => setDialog({ kind: "archive-sub", sub: s })}
                onDeleteTouch={(touchId) =>
                  setDialog({ kind: "delete-touch", sub: s, touchId })
                }
                onDeleteItem={(itemId) =>
                  setDialog({ kind: "delete-item", sub: s, itemId })
                }
                onToggleItem={async (item) => {
                  const next = item.sentAt
                    ? null
                    : new Date().toISOString();
                  try {
                    await patchItem(s.id, item.id, { sent_at: next });
                    bumpRefresh();
                  } catch {
                    setToast("⚠ Item toggle failed");
                  }
                }}
                onRetryItemZohoLink={async (item) => {
                  try {
                    await retryItemZohoLink(s.id, item.id, {
                      deal_name: dealName,
                    });
                    bumpRefresh();
                    setToast("✓ Zoho task linked");
                  } catch (err) {
                    setToast(
                      err instanceof ApiError
                        ? `⚠ Retry failed: ${err.code}`
                        : "⚠ Retry failed",
                    );
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Dialogs — single slot, mutually exclusive. */}
      {dialog?.kind === "add" && (
        <AddSubmissionDialog
          dealId={dealId}
          dealName={dealName ?? "this deal"}
          onCreated={async () => {
            setDialog(null);
            bumpRefresh();
            setToast(`✓ Added to ${dealLabel}`);
          }}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "status" && (
        <ChangeStatusDialog
          submissionId={dialog.sub.id}
          lenderName={dialog.sub.lenderName}
          currentStatus={dialog.sub.status}
          onUpdated={async (newStatus) => {
            setDialog(null);
            bumpRefresh();
            setToast(`✓ Status → ${STATUS_PILL_LABELS[newStatus]}`);
          }}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "touch" && (
        <AddTouchDialog
          submissionId={dialog.sub.id}
          lenderName={dialog.sub.lenderName}
          onCreated={async () => {
            setDialog(null);
            bumpRefresh();
            setToast("✓ Touch logged");
          }}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "item" && (
        <AddItemDialog
          submissionId={dialog.sub.id}
          lenderName={dialog.sub.lenderName}
          dealName={dealName}
          onCreated={async ({ zohoLinkWarning }) => {
            setDialog(null);
            bumpRefresh();
            setToast(
              zohoLinkWarning
                ? "⚠ Item added but Zoho task link failed"
                : "✓ Item added",
            );
          }}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "delete-touch" && (
        <ConfirmDeleteDialog
          title="Delete touch?"
          message="This touch will be removed from the chronology. (Soft delete — preserved in the database for audit.)"
          onConfirm={async () => {
            await archiveTouch(dialog.sub.id, dialog.touchId);
            setDialog(null);
            bumpRefresh();
            setToast("✓ Touch deleted");
          }}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "delete-item" && (
        <ConfirmDeleteDialog
          title="Delete item?"
          message="This item will be removed from the checklist. (Soft delete — preserved in the database for audit.)"
          onConfirm={async () => {
            await archiveItem(dialog.sub.id, dialog.itemId);
            setDialog(null);
            bumpRefresh();
            setToast("✓ Item deleted");
          }}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "archive-sub" && (
        <ConfirmDeleteDialog
          title={`Archive ${dialog.sub.lenderName}?`}
          message="This submission will be hidden from the active list. Touches, items, and terms history are preserved."
          confirmLabel="ARCHIVE"
          onConfirm={async () => {
            await archiveSubmission(dialog.sub.id);
            setDialog(null);
            // Collapse the row before refreshing — it's about to disappear.
            setExpandedId(null);
            bumpRefresh();
            setToast(`✓ ${dialog.sub.lenderName} archived`);
          }}
          onCancel={() => setDialog(null)}
        />
      )}

      {toast && <div className="edge-toast">{toast}</div>}
    </TabPanel>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="submissions-empty">
      <div className="submissions-empty-headline">
        This deal hasn't been pitched yet.
      </div>
      <div className="submissions-empty-body">
        When you pitch lenders, track each submission here:
        <br />
        who has what, items requested, terms received,
        <br />
        who declined and who's still in the running.
      </div>
      <button
        className="submissions-add-btn submissions-empty-cta"
        onClick={onAdd}
      >
        + ADD SUBMISSION
      </button>
    </div>
  );
}

interface RowProps {
  summary: SubmissionSummary;
  expanded: boolean;
  refreshTick: number;
  onToggle: () => void;
  onChangeStatus: () => void;
  onAddTouch: () => void;
  onAddItem: () => void;
  onArchiveSub: () => void;
  onDeleteTouch: (touchId: string) => void;
  onDeleteItem: (itemId: string) => void;
  onToggleItem: (item: SubmissionItem) => void;
  onRetryItemZohoLink: (item: SubmissionItem) => void;
}

function SubmissionRow({
  summary,
  expanded,
  refreshTick,
  onToggle,
  onChangeStatus,
  onAddTouch,
  onAddItem,
  onArchiveSub,
  onDeleteTouch,
  onDeleteItem,
  onToggleItem,
  onRetryItemZohoLink,
}: RowProps) {
  const [detail, setDetail] = useState<SubmissionDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const blurb = summaryBlurb(summary);

  // Lazy fetch on expand; also refetch whenever refreshTick bumps if we're
  // currently expanded. Collapsed rows don't re-fetch — they just stale.
  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    setDetailError(null);
    fetchSubmissionDetail(summary.id)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err) => {
        if (cancelled) return;
        setDetailError(
          err instanceof ApiError
            ? `${err.code} (HTTP ${err.status})`
            : err instanceof Error
              ? err.message
              : "fetch_failed",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [expanded, refreshTick, summary.id]);

  // Stop click bubbling from action buttons so the row doesn't toggle.
  const stop = (handler: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handler();
  };

  return (
    <div
      className={clsx(
        "submission-row",
        summary.status === "backup" && "is-backup",
        expanded && "expanded",
      )}
    >
      <div className="submission-row-main" onClick={onToggle}>
        <div className="submission-row-lender">{summary.lenderName}</div>
        <span
          className={clsx(
            "status-pill",
            `status-${summary.status.replace("_", "-")}`,
          )}
        >
          {STATUS_PILL_LABELS[summary.status]}
        </span>
        {blurb && <div className="submission-row-summary">{blurb}</div>}
        <div className="submission-row-age">{timeAgo(summary.lastTouchAt)}</div>
        <span className="submission-expand-icon">▸</span>
      </div>

      <div className="submission-detail">
        <div className="submission-detail-inner">
          {detailError && (
            <div className="write-confirm-error">
              ⚠ Failed to load detail: {detailError}
            </div>
          )}
          {!detail && !detailError && (
            <div className="submissions-loading submissions-loading-small">
              <span className="chat-thinking-dot" />
              <span className="chat-thinking-dot" />
              <span className="chat-thinking-dot" />
            </div>
          )}
          {detail && (
            <DetailBody
              detail={detail}
              onDeleteTouch={onDeleteTouch}
              onDeleteItem={onDeleteItem}
              onToggleItem={onToggleItem}
              onRetryItemZohoLink={onRetryItemZohoLink}
            />
          )}

          {/* Action row — always visible inside the expanded panel. */}
          <div className="submission-action-row">
            <button
              className="submission-action-btn touch"
              onClick={stop(onAddTouch)}
            >
              + TOUCH
            </button>
            <button
              className="submission-action-btn status"
              onClick={stop(onChangeStatus)}
            >
              CHANGE STATUS
            </button>
            <button
              className="submission-action-btn item"
              onClick={stop(onAddItem)}
            >
              + ITEM
            </button>
            <button
              className="submission-archive-link"
              onClick={stop(onArchiveSub)}
              title="Archive this submission"
            >
              archive ✕
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface DetailBodyProps {
  detail: SubmissionDetail;
  onDeleteTouch: (touchId: string) => void;
  onDeleteItem: (itemId: string) => void;
  onToggleItem: (item: SubmissionItem) => void;
  onRetryItemZohoLink: (item: SubmissionItem) => void;
}

function DetailBody({
  detail,
  onDeleteTouch,
  onDeleteItem,
  onToggleItem,
  onRetryItemZohoLink,
}: DetailBodyProps) {
  return (
    <>
      {detail.status === "declined" && detail.declineReason && (
        <div className="submission-reason-block">
          <div className="submission-reason-label">DECLINE REASON</div>
          <div className="submission-reason-text">"{detail.declineReason}"</div>
        </div>
      )}
      {detail.status === "backup" && detail.backupReason && (
        <div className="submission-reason-block submission-reason-block-backup">
          <div className="submission-reason-label">BACKUP REASON</div>
          <div className="submission-reason-text">"{detail.backupReason}"</div>
        </div>
      )}

      {detail.items.length > 0 && (
        <ItemsList
          items={detail.items}
          onDeleteItem={onDeleteItem}
          onToggleItem={onToggleItem}
          onRetryItemZohoLink={onRetryItemZohoLink}
        />
      )}
      {detail.terms.length > 0 && <TermsCards terms={detail.terms} />}
      <TouchLog touches={detail.touches} onDeleteTouch={onDeleteTouch} />
    </>
  );
}

const DIRECTION_ICON: Record<string, string> = {
  inbound: "→",
  outbound: "←",
  edge: "◆",
};
const DIRECTION_LABEL: Record<string, string> = {
  inbound: "FROM LENDER",
  outbound: "TO LENDER",
  edge: "EDGE",
};

function TouchLog({
  touches,
  onDeleteTouch,
}: {
  touches: SubmissionTouch[];
  onDeleteTouch: (touchId: string) => void;
}) {
  return (
    <div className="submission-touch-log">
      <div className="submission-section-title">TOUCH LOG</div>
      {touches.length === 0 ? (
        <div className="submission-empty-section">No touches logged yet.</div>
      ) : (
        touches.map((t) => (
          <div className="submission-touch-entry" key={t.id}>
            <div className="submission-touch-meta">
              <span
                className={clsx("submission-touch-dir", `dir-${t.direction}`)}
                title={DIRECTION_LABEL[t.direction]}
              >
                {DIRECTION_ICON[t.direction]} {DIRECTION_LABEL[t.direction]}
              </span>
              {t.statusAtTime && (
                <span className="submission-touch-status">
                  · {STATUS_PILL_LABELS[t.statusAtTime as SubmissionStatus]}
                </span>
              )}
              <span className="submission-touch-age">
                · {timeAgo(t.occurredAt)}
              </span>
              <button
                className="submission-row-delete"
                onClick={() => onDeleteTouch(t.id)}
                title="Delete touch"
                aria-label="Delete touch"
              >
                ✕
              </button>
            </div>
            <div className="submission-touch-body">{t.body}</div>
          </div>
        ))
      )}
    </div>
  );
}

function ItemsList({
  items,
  onDeleteItem,
  onToggleItem,
  onRetryItemZohoLink,
}: {
  items: SubmissionItem[];
  onDeleteItem: (itemId: string) => void;
  onToggleItem: (item: SubmissionItem) => void;
  onRetryItemZohoLink: (item: SubmissionItem) => void;
}) {
  return (
    <div className="submission-items-list">
      <div className="submission-section-title">
        ITEMS REQUESTED ·{" "}
        <span className="submission-section-count">
          {items.filter((i) => i.sentAt).length}/{items.length}
        </span>
      </div>
      {items.map((it) => (
        <div
          key={it.id}
          className={clsx("submission-item-row", it.sentAt && "is-sent")}
        >
          <button
            className="submission-item-checkbox"
            onClick={() => onToggleItem(it)}
            aria-pressed={!!it.sentAt}
            title={it.sentAt ? "Mark as not sent" : "Mark as sent"}
          >
            {it.sentAt ? "☑" : "☐"}
          </button>
          <span className="submission-item-label">{it.label}</span>
          {it.zohoTaskId === null && (
            <button
              className="submission-item-zoho-warn"
              onClick={() => onRetryItemZohoLink(it)}
              title="Zoho task link missing — click to retry"
              aria-label="Retry Zoho task link"
            >
              ⚠
            </button>
          )}
          {it.sentAt && (
            <span className="submission-item-sent">
              sent {timeAgo(it.sentAt)}
            </span>
          )}
          <button
            className="submission-row-delete"
            onClick={() => onDeleteItem(it.id)}
            title="Delete item"
            aria-label="Delete item"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

function TermsCards({ terms }: { terms: SubmissionTermsVersion[] }) {
  return (
    <div className="submission-terms-list">
      <div className="submission-section-title">TERMS</div>
      {terms.map((t) => (
        <div className="submission-terms-card" key={t.id}>
          <div className="submission-terms-header">
            <span className="submission-terms-version">{t.versionLabel}</span>
            <span className="submission-terms-age">
              · received {timeAgo(t.receivedAt)}
            </span>
          </div>
          <div className="submission-terms-grid">
            {t.rate && <Field label="Rate" value={t.rate} />}
            {t.points && <Field label="Points" value={t.points} />}
            {t.termMonths && <Field label="Term" value={`${t.termMonths} mo`} />}
            {t.ltv && <Field label="LTV" value={t.ltv} />}
            {t.dscrMin && <Field label="DSCR" value={t.dscrMin} />}
            {t.prepay && <Field label="Prepay" value={t.prepay} />}
            {t.loanAmountOffered && (
              <Field
                label="Loan Amount"
                value={`$${t.loanAmountOffered.toLocaleString()}`}
              />
            )}
          </div>
          {t.notes && <div className="submission-terms-notes">{t.notes}</div>}
          {t.pdfFilename && (
            <div className="submission-terms-pdf">
              📎 {t.pdfFilename}
              <span className="submission-terms-pdf-soon"> (download in S.4)</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="submission-terms-field">
      <span className="submission-terms-field-label">{label}</span>
      <span className="submission-terms-field-value">{value}</span>
    </div>
  );
}
